import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import useThemeStyles from '../theme/useThemeStyles';

function WeddingVows({ embedded }) {
  const styles = useThemeStyles();
  const [myVows, setMyVows] = useState('');
  const [myLabel, setMyLabel] = useState('My Vows');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [_loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    fetchVows();
  }, []);

  const fetchVows = async () => {
    try {
      setLoading(true);
      const [vowsRes, partnersRes] = await Promise.all([
        api.get('/api/vows'),
        api.get('/api/wedding-details/partners').catch(() => ({ data: {} }))
      ]);
      const data = vowsRes.data || {};
      setMyVows(data.myVows || '');
      const key = data.partnerKey || 'partnerOne';
      const partners = partnersRes.data || {};
      const p = key === 'partnerOne' ? partners.partnerOne : partners.partnerTwo;
      const name = p?.preferredName || p?.fullName || (key === 'partnerOne' ? 'Partner One' : 'Partner Two');
      setMyLabel(`${name}'s Vows`);
    } catch (err) {
      setError('Failed to load vows');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/vows', { myVows });
      setSuccess('Vows saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save vows');
    } finally {
      setSaving(false);
    }
  };

  const _getAISuggestions = async () => {
    try {
      setLoadingAI(true);
      const response = await api.get('/api/vows/suggestions');
      setAiSuggestions(response.data.suggestions);
    } catch (error) {
      setError('Failed to get AI suggestions');
    } finally {
      setLoadingAI(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={embedded ? { p: 0 } : styles.container.page}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, px: 2 }}>
        Vows
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
        Write your personal vows in a private space. These are for your eyes only.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={styles.card.default}>
        <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
          <Typography variant="h6" sx={styles.text.sectionTitle}>
            {myLabel}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={myVows}
            onChange={(e) => setMyVows(e.target.value)}
            placeholder="Write your heartfelt vows here..."
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            {myVows.split(' ').filter(w => w).length} words
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ ...styles.layout.flexCenter, mt: 3, gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Vows'}
        </Button>
      </Box>

      {aiSuggestions && (
        <Card sx={{ ...styles.card.default, mt: 3 }}>
          <CardContent sx={styles.spacing.card}>
            <Typography variant="h6" sx={styles.text.sectionTitle}>
              AI Vow Inspirations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use these suggestions as inspiration for your own personal vows
            </Typography>
            
            <Grid container spacing={2}>
              {aiSuggestions.map((suggestion, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', position: 'relative' }}>
                    <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                      "{suggestion}"
                    </Typography>
                    <Box sx={styles.layout.flexBetween}>
                      <Chip 
                        label={`Example ${index + 1}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                      <Button
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={() => copyToClipboard(suggestion)}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default WeddingVows; 